import React, { useContext } from 'react';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import { BsChevronRight, BsTelephone, BsCheck } from 'react-icons/bs';

import { DealerSearchContext } from '../../contexts/DealerSearchContext';
import { LocaleContext } from '../../contexts/LocaleContext';

import { Dealer } from '../../models/algolia/Dealer';

import getCookieDomain from '@repo/utils/getCookieDomain';

import { Button } from '../shadcn/Button';
import LinkComponent from '@repo/ui-components/LinkComponent';
import breakpoints from '@repo/ui-components/breakpoints';
import sharedStyles from '@repo/ui-components/styles';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import parsePhoneNumber from './parsePhoneNumber';

const SearchResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      padding: 1.5rem 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.inputBorder};
      &:last-child {
        border-bottom: none;
      }
    }
  }
`;

const DealerName = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.defaultFont};
`;

const DealerText = styled.div`
  font-size: 1rem;
  margin-bottom: 0.65rem;
  display: flex;
  color: ${({ theme }) => theme.colors.defaultFont};
  && a {
    color: ${({ theme }) => theme.colors.link};
    text-decoration: underline;
  }
  .tab-focus-trap {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
  svg {
    margin-right: 0.5rem;
  }
`;

const DealerLink = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  font-size: ${sharedStyles.fontSize.base};
  font-weight: ${sharedStyles.fontWeight.bold};
  span {
    margin-right: ${sharedStyles.spacing['spacing-1xs']};
  }
  && a {
    text-decoration: none;
  }
`;

const DealerSelectContainer = styled.div`
  display: flex;
  flex-direction: row;
  label {
    cursor: pointer;
    // don't make full width
    flex: 0 0 auto;
    background: #eee;
    padding: 0.25rem 1rem;
    border-radius: 0.25rem;
  }
  input {
    margin-right: 0.25rem;
  }
  button span {
    font-size: 90%;
  }
`;

const SelectedDealerNotice = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 0.5rem;
  font-weight: bold;
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.brandPrimary};
`;

interface DealerSearchContextProps {
  setDealerSearchResults: React.Dispatch<React.SetStateAction<boolean>>;
  fetchStoredDealerData: (options?: { selected?: boolean }) => Promise<void>;
  storedDealer: Dealer | null;
  rememberStoredDealerPostalCode?: (dealerId: number | string) => void;
}

export interface SearchResultsProps {
  dealerSearchResults: Dealer[];
  displayMode: string;
  setDealerSearchActive: React.Dispatch<React.SetStateAction<boolean>>;
  hideSelectedDealerNotice?: boolean;
}

function SearchResults({
  dealerSearchResults,
  displayMode = '',
  setDealerSearchActive,
  hideSelectedDealerNotice = false,
}: Readonly<SearchResultsProps>) {
  const { tt } = useUiComponentsInternationalContext();

  const dealerContext =
    useContext<DealerSearchContextProps>(DealerSearchContext);
  const { currency } = useContext(LocaleContext);
  const isDesktop = window.matchMedia(`(min-width: ${breakpoints.lg})`).matches;

  const { storedDealer } = dealerContext;

  const {
    fetchStoredDealerData,
    setDealerSearchResults,
    rememberStoredDealerPostalCode,
  } = dealerContext;

  // Move stored dealer to top of search results
  if (storedDealer) {
    const storedDealerIndex = dealerSearchResults.findIndex(
      dealer => dealer.id === storedDealer.id
    );

    if (storedDealerIndex > -1) {
      dealerSearchResults.splice(storedDealerIndex, 1);
      dealerSearchResults.unshift(storedDealer);
    }
  }

  return (
    <SearchResultsContainer>
      <ul>
        {dealerSearchResults.map(dealer => {
          // parse phone number so we can format it correctly
          const dealerPhoneObject = parsePhoneNumber(`${dealer.phone}`);

          const isSelectedDealer =
            storedDealer && storedDealer.id === dealer.id;

          return (
            <li key={dealer.id}>
              {
                // if this is the current stored dealer, show a message
                isSelectedDealer && !hideSelectedDealerNotice && (
                  <SelectedDealerNotice>
                    <BsCheck size="1.5rem" />{' '}
                    {tt(s => s.dealerCommon.selectedDealer)}
                  </SelectedDealerNotice>
                )
              }

              <DealerName>{dealer.name}</DealerName>
              {dealer.mainServiceArea && (
                <DealerText>Serving {dealer.mainServiceArea}</DealerText>
              )}

              {displayMode !== 'compact' && (
                <DealerText>
                  <BsTelephone
                    size={24}
                    className="fill-primary"
                    aria-hidden="true"
                  />
                  <a href={dealerPhoneObject.getURI()}>
                    <strong>{dealerPhoneObject.formatNational()}</strong>
                  </a>
                </DealerText>
              )}
              <DealerText>
                {dealer.address.line1 && (
                  <>
                    {dealer.address.line1}
                    <br />
                  </>
                )}
                {dealer.address.line2 && (
                  <>
                    {dealer.address.line2}
                    <br />
                  </>
                )}
                {dealer.address.city &&
                  dealer.address.state &&
                  dealer.address.zip && (
                    <>
                      {dealer.address.city} {dealer.address.state}{' '}
                      {dealer.address.zip}
                    </>
                  )}
              </DealerText>

              {(dealer?.address?.line1 || dealer?.address?.line2) &&
                displayMode !== 'compact' && (
                  <DealerText>
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${
                        dealer.address.line1 || ''
                      }+${dealer.address.line2 || ''}+${dealer.address.city}+${
                        dealer.address.state
                      }+${dealer.address.zip}+${dealer.address.country}`}
                      target={isDesktop ? '_blank' : '_self'}
                      rel="noopener noreferrer"
                    >
                      Directions
                    </a>
                  </DealerText>
                )}
              {dealer.slug && displayMode !== 'compact' && (
                <DealerText>
                  <DealerLink>
                    <LinkComponent
                      aria-label={tt(
                        s => s.dealerCommon.visitDealerPageAriaLabel,
                        { dealerName: dealer.name }
                      )}
                      to={
                        currency === 'CAD'
                          ? `/en-ca/dealer/${dealer.slug}`
                          : `/dealer/${dealer.slug}`
                      }
                      onClick={() => {
                        setDealerSearchActive(false);
                      }}
                    >
                      <span>{tt(s => s.dealerCommon.visitDealerPageLink)}</span>
                    </LinkComponent>
                    <BsChevronRight
                      size={16}
                      className="fill-primary"
                      aria-hidden="true"
                    />
                  </DealerLink>
                </DealerText>
              )}
              {!isSelectedDealer && (
                <DealerSelectContainer>
                  <Button
                    aria-label={tt(s => s.dealerCommon.selectDealerAriaLabel, {
                      dealerName: dealer.name,
                    })}
                    id="dealer-select-button"
                    onClick={async () => {
                      // if single dealer result, set dealerId in cookie

                      if (typeof window !== 'undefined') {
                        // set dealerId in cookie
                        Cookies.set('dealerId', dealer?.id?.toString(), {
                          domain: getCookieDomain(),
                          expires: new Date(
                            Date.now() + 365 * 24 * 60 * 60 * 1000
                          ),
                        });

                        // pair the dealer with the ZIP that was just searched,
                        // so other pages (e.g. Find My Fit pricing) can show it
                        rememberStoredDealerPostalCode?.(dealer.id);

                        // update storedDealer with fresh dealer data from
                        // Algolia; `selected` records that the shopper picked it
                        await fetchStoredDealerData({ selected: true });
                        setDealerSearchResults(null);
                        // close the modal
                        setDealerSearchActive(false);
                      }
                    }}
                    className="gap-1"
                  >
                    <span id="search-label">
                      {tt(s => s.dealerCommon.selectThisDealerButton)}
                    </span>
                  </Button>
                </DealerSelectContainer>
              )}
            </li>
          );
        })}
      </ul>
    </SearchResultsContainer>
  );
}

export default SearchResults;
